import { MenuButton, MenuItem, Menu, useMenuStore, MenuProvider, useStoreState, MenuStore } from '@ariakit/react';
import { formatDistanceToNow, format } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router';

import { Notification, NOTIFICATION_PAGE_SIZE } from '@data';
import { useUiNodeTitle } from '@hooks';
import {
    useMarkNotificationAsRead,
    useUiNotification,
    usePaginatedNotifications,
    useUserName,
    useThreadMessageUnreadNotifications,
} from '@queries';
import { activeUserIdAtom } from '@store';
import {
    BellIcon,
    ChatTextIcon,
    CircularCloseIcon,
    colors,
    DrawingIcon,
    SpeakerIcon,
    Box,
    Button,
    Heading3,
    Heading6,
    Text,
    Spinner,
    u,
} from '@ui';

import { SkeletonMenuItem } from './components/SkeletonMenuItem';
import * as css from './NotificationMenu.css';

export type NotificationMenuProps = {
    getLinkToSwing: (activityId: number, swingUuid: string) => string;
};

export const NotificationMenu = ({ getLinkToSwing }: NotificationMenuProps) => {
    const menu = useMenuStore({ placement: 'bottom-end' });
    const { data: unreadNotifications } = useThreadMessageUnreadNotifications();
    const open = useStoreState(menu, 'open');

    const activeUserId = useAtomValue(activeUserIdAtom);
    if (!activeUserId) {
        return null;
    }

    const hasUnread = !!unreadNotifications?.sourceUsers?.[activeUserId]?.length;

    return (
        <MenuProvider store={menu}>
            <MenuButton store={menu} className={css.notificationMenuButton}>
                <BellIcon size={u(3)} color={colors.blue[500]} />
                {hasUnread && <span className={css.notificationMark} />}
            </MenuButton>
            {open ? <NotificationList menu={menu} activeUserId={activeUserId} getLinkToSwing={getLinkToSwing} /> : null}
        </MenuProvider>
    );
};

type NotificationListProps = NotificationMenuProps & {
    menu: MenuStore;
    activeUserId: string;
};

export const NotificationList = ({ menu, activeUserId, getLinkToSwing: linkToFullActivity }: NotificationListProps) => {
    const { ref } = useInView({
        threshold: 0.5,
        initialInView: true,
        onChange: (inView) => {
            if (inView && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        },
    });

    const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = usePaginatedNotifications(activeUserId);
    const notifications = data?.pages.flat() ?? [];
    const noNotifications = notifications.length === 0;
    // If the user has to load two times he gets a end of list message that he has reached the end
    const shouldShowFooterMessage =
        notifications.length > NOTIFICATION_PAGE_SIZE && !hasNextPage && !isFetchingNextPage;

    return (
        <Menu store={menu} className={css.menu} portal shift={0} gutter={-52}>
            <Box
                display="flex"
                paddingInlineStart={u(2)}
                paddingInlineEnd={u(1)}
                paddingBlockStart={u(2.5)}
                paddingBlockEnd={u(2)}
                alignItems="center"
                justifyContent="space-between"
            >
                <Heading3>Notifications</Heading3>

                <Box>
                    <button onClick={menu.hide}>
                        <CircularCloseIcon width={24} color={colors.bluegray[600]} />
                    </button>
                </Box>
            </Box>

            {noNotifications && <NoNotifications isLoading={isLoading} />}
            <div className={css.scrollArea}>
                <Box>
                    {notifications.map((notification) => (
                        <NotificationItem
                            key={notification.id}
                            notification={notification}
                            activeUserId={activeUserId}
                            getLinkToSwing={linkToFullActivity}
                        />
                    ))}
                    <div ref={ref} style={{ paddingBottom: hasNextPage ? 32 : 0 }} />
                    {shouldShowFooterMessage ? <EndItem /> : null}
                </Box>
            </div>
            {!noNotifications && <NotificationListFooter activeUserId={activeUserId} />}
        </Menu>
    );
};

type NotificationListFooterProps = Omit<NotificationMenuProps, 'getLinkToSwing'> & {
    activeUserId: string;
};

const NotificationListFooter = ({ activeUserId }: NotificationListFooterProps) => {
    const { data: unreadNotifications } = useThreadMessageUnreadNotifications();
    const { mutateAsync: markAllNotificationAsRead } = useMarkNotificationAsRead(activeUserId);
    const unread = unreadNotifications?.sourceUsers?.[activeUserId]?.length ?? 0;
    return (
        <Box display="flex" width="100%" style={{ height: 64 }} justifyContent="center" alignItems="center">
            {unread > 0 ? (
                <Button size="small" onClick={() => markAllNotificationAsRead(undefined)}>
                    {unread > 1 ? 'Mark all as read' : 'Mark as read'}
                </Button>
            ) : (
                <Box display="flex" gap={u(1)} alignItems="center">
                    <Text color="primary" style={{ fontSize: '0.75rem' }}>
                        No unread messages
                    </Text>
                </Box>
            )}
        </Box>
    );
};

const NoNotifications = ({ isLoading }: { isLoading: boolean }) => (
    <Box height="max-content" paddingBlockEnd={u(2)}>
        <header className={css.notificationHeader()}></header>
        {isLoading ? (
            <Spinner size="m" />
        ) : (
            <Heading6
                textAlign="center"
                paddingBlockStart={u(1.5)}
                paddingBlockEnd={u(3)}
                marginInline={u(3)}
                color="primary"
                className={css.notificationParagraphEmpty}
            >
                When someone leaves a comment on one of your swings, you will get a notification here.
            </Heading6>
        )}
    </Box>
);

type NotificationItemProps = NotificationMenuProps & {
    notification: Notification;
    activeUserId: string;
};

export const NotificationItem = ({
    notification,
    activeUserId,
    getLinkToSwing: linkToFullActivity,
}: NotificationItemProps) => {
    const { data: notificationItem, isLoading, isError } = useUiNotification(notification);
    const { data: sourceUserName } = useUserName(notificationItem?.createdBy ?? undefined);
    const { mutateAsync: markNotificationAsRead } = useMarkNotificationAsRead(activeUserId);

    const getTitle = useUiNodeTitle();

    if (isError && !notificationItem) {
        return <ErrorItem notification={notification} />;
    }

    if (isLoading || !notificationItem) {
        return <SkeletonMenuItem />;
    }

    const {
        swingUuid,
        activityId,
        swingActivitySequence,
        swingCreatedAt,
        readAt,
        description,
        createdAt,
        hasDrawing,
        audioUrl,
        threadId,
        linkNodeId,
    } = notificationItem;

    const handleOnLinkClick = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        evt.stopPropagation();
        markNotificationAsRead([notification.id]);
    };
    const title = getTitle(linkNodeId);
    const renderTitle = title.length > 0 ? title : 'Comment';
    return (
        <MenuItem
            onClick={handleOnLinkClick}
            render={
                swingUuid ? (
                    <Link
                        to={{
                            pathname: linkToFullActivity(activityId, swingUuid),
                            search: `?threadId=${threadId}`,
                        }}
                    />
                ) : (
                    <span />
                )
            }
            className={css.notification({ unread: !readAt })}
        >
            <header className={css.notificationHeader({ unread: !readAt })}>
                <span>
                    {format(swingCreatedAt, 'P')} S-{swingActivitySequence}
                </span>
                {!readAt && <span className={css.notificationMarkTitle} />}
            </header>
            <h4 className={css.notificationTitle({ unread: !readAt })}>{renderTitle}</h4>
            <Text variant="pSmall" className={css.notificationParagraph}>
                <strong>{sourceUserName}</strong>: {description}
            </Text>
            <footer className={css.notificationSince({ unread: !readAt })}>
                {formatDistanceToNow(createdAt)}

                <span className={css.footerIcons}>
                    {hasDrawing && <DrawingIcon size={u(2)} />}
                    {!!description && <ChatTextIcon size={u(2)} />}
                    {!!audioUrl && <SpeakerIcon size={u(2)} />}
                </span>
            </footer>
        </MenuItem>
    );
};

export const EndItem = () => (
    <Box className={css.stickyInfo}>
        <header className={css.notificationHeader()}></header>

        <Heading6
            textAlign="center"
            paddingBlockStart={u(1.5)}
            paddingBlockEnd={u(3)}
            marginInline={u(3)}
            color="brand"
            className={css.notificationParagraphEmpty}
        >
            You're all caught up!
        </Heading6>
    </Box>
);

export const ErrorItem = ({ notification }: { notification: Notification }) => (
    <Box px={u(2)} py={u(2)} className={css.error}>
        <header className={css.notificationHeader({ error: true })}>
            <span>Error Loading notifications {notification.id}</span>
        </header>
        <h4 className={css.notificationTitle({ error: true })}>Notification Error</h4>
        <p className={css.notificationParagraph}>
            A Error occurred while trying to load notifications `{notification.entityType}`
        </p>
    </Box>
);
